import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Compass, ArrowLeft, Loader2, Users, Radio, BadgeCheck, ShieldCheck, KeyRound, Coins, Search, X, Flame, Sparkles, TrendingUp, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/common/Avatar";
import { ConnectionButton } from "@/components/common/ConnectionButton";

const TABS = [
  { key: "bloqs", label: "BLOQs", icon: Users },
  { key: "channels", label: "Channels", icon: Radio },
  { key: "people", label: "People", icon: BadgeCheck },
];
const FILTERS = {
  bloqs: ["trending", "verified", "new"],
  channels: ["trending", "verified", "new"],
  people: ["top-trust", "verified"],
};
const FILTER_LABEL = { trending: "Trending", verified: "Verified", new: "New", "top-trust": "Top trust" };
const BAND_ACCENT = { "High Trust": "text-bloq-mint", Trusted: "text-bloq-mint", Established: "text-bloq-blue", Watch: "text-amber-400", New: "text-bloq-muted" };

// Recent Discover searches — remembered per-device so users can tap to re-run one.
const RECENTS_KEY = "bloq_discover_recent_searches";
const loadRecents = () => {
  try {
    const v = JSON.parse(localStorage.getItem(RECENTS_KEY));
    return Array.isArray(v) ? v.slice(0, 8) : [];
  } catch {
    return [];
  }
};

// "New" = backend-flagged OR created within the last 7 days (client-derived from created_at).
const isNew = (item) => {
  if (item?.is_new) return true;
  if (!item?.created_at) return false;
  const t = new Date(item.created_at).getTime();
  return Number.isFinite(t) && Date.now() - t < 7 * 864e5;
};
// Weekly growth delta — self-hiding shell; lights up when Mobile adds member_delta_7d / subscriber_delta_7d.
const weekDelta = (item) => {
  const n = item?.member_delta_7d ?? item?.subscriber_delta_7d ?? item?.week_delta ?? null;
  return typeof n === "number" && n > 0 ? n : null;
};

const TrendingPill = () => (
  <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-400/12 px-1.5 py-0.5 text-[10px] font-bold text-amber-300" data-testid="discover-trending-badge">
    <Flame className="h-2.5 w-2.5" /> Trending
  </span>
);
const NewPill = () => (
  <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-bloq-blue/15 px-1.5 py-0.5 text-[10px] font-bold text-bloq-blue" data-testid="discover-new-badge">
    <Sparkles className="h-2.5 w-2.5" /> New
  </span>
);
const DeltaPill = ({ n }) => (
  <span className="inline-flex items-center gap-0.5 rounded-full bg-bloq-mint/10 px-1.5 py-0.5 font-semibold text-bloq-mint" data-testid="discover-delta-badge">
    <TrendingUp className="h-2.5 w-2.5" /> +{n} this week
  </span>
);

const fetchFor = (tab, filter, cursor, q) =>
  tab === "bloqs" ? api.discoverBloqs(filter, cursor, q)
    : tab === "channels" ? api.discoverChannels(filter, cursor, q)
      : api.discoverPeople(filter, cursor, q);

function TrustChip({ score, band }) {
  if (score == null && !band) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border border-white/12 px-2 py-0.5 text-[10px] font-bold", BAND_ACCENT[band] || "text-bloq-muted")}>
      <ShieldCheck className="h-3 w-3" /> {score ?? "—"} · {band || "—"}
    </span>
  );
}

function BloqCard({ c, onOpen }) {
  const delta = weekDelta(c);
  return (
    <button onClick={onOpen} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-3.5 text-left transition-colors hover:border-bloq-mint/40 hover:bg-white/[0.03]" data-testid={`discover-bloq-${c.id}`}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-bloq-s2 text-xl">{c.emoji || "🟢"}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold">{c.name}</span>
          {c.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-bloq-mint" />}
          {c.trending && <TrendingPill />}
          {isNew(c) && <NewPill />}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pt-1 text-[11px] text-bloq-muted">
          <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {c.member_count} member{c.member_count === 1 ? "" : "s"}</span>
          {delta && <DeltaPill n={delta} />}
          {c.gate?.token_gated && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-400/10 px-1.5 py-0.5 font-semibold text-amber-300"><Coins className="h-2.5 w-2.5" /> Token</span>}
          {c.gate?.require_verified && <span className="inline-flex items-center gap-0.5 rounded-full bg-bloq-mint/10 px-1.5 py-0.5 font-semibold text-bloq-mint"><KeyRound className="h-2.5 w-2.5" /> Verified-only</span>}
          <TrustChip score={c.score} band={c.band} />
        </div>
      </div>
      {c.is_member && <span className="shrink-0 text-[11px] font-semibold text-bloq-mint">Joined</span>}
    </button>
  );
}

function ChannelCard({ c, onOpen }) {
  const delta = weekDelta(c);
  return (
    <button onClick={onOpen} className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-3.5 text-left transition-colors hover:border-bloq-blue/40 hover:bg-white/[0.03]" data-testid={`discover-channel-${c.id}`}>
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-bloq-s2 text-xl">{c.emoji || "📡"}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold">{c.name}</span>
          {c.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-bloq-blue" />}
          {c.trending && <TrendingPill />}
          {isNew(c) && <NewPill />}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 pt-1 text-[11px] text-bloq-muted">
          <span className="truncate">@{c.handle}</span>
          <span className="inline-flex items-center gap-1"><Radio className="h-3 w-3" /> {c.subscriber_count} sub{c.subscriber_count === 1 ? "" : "s"}</span>
          {delta && <DeltaPill n={delta} />}
          <TrustChip score={c.score} band={c.band} />
        </div>
      </div>
      {c.subscribed && <span className="shrink-0 text-[11px] font-semibold text-bloq-blue">Subscribed</span>}
    </button>
  );
}

function PersonCard({ p, onOpen }) {
  return (
    <div className="flex w-full items-center gap-3 rounded-2xl border border-white/8 bg-bloq-s1 p-3.5 text-left transition-colors hover:border-bloq-mint/40 hover:bg-white/[0.03]" data-testid={`discover-person-${p.handle}`}>
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid={`discover-person-open-${p.handle}`}>
        <span className="shrink-0" data-testid={`discover-person-avatar-${p.handle}`}>
          <Avatar user={p} size="md" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-semibold">{p.display_name || p.handle}</span>
            {p.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-bloq-mint" />}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-[11px] text-bloq-muted">
            <span className="truncate">@{p.handle}</span>
            <TrustChip score={p.score} band={p.band} />
          </div>
        </div>
      </button>
      {!p.is_me && <ConnectionButton user={p} />}
    </div>
  );
}

export default function DiscoverPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "bloqs";
  const [filter, setFilter] = useState(FILTERS[tab][0]);
  const activeFilter = FILTERS[tab].includes(filter) ? filter : FILTERS[tab][0];
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [recents, setRecents] = useState(loadRecents);

  // Debounce the query so we hit the server once the user pauses typing.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const pushRecent = (term) => {
    const t = (term || "").trim();
    if (t.length < 2) return;
    setRecents((prev) => {
      const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 8);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      return next;
    });
  };
  const removeRecent = (term) =>
    setRecents((prev) => {
      const next = prev.filter((x) => x !== term);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      return next;
    });
  const clearRecents = () => {
    setRecents([]);
    localStorage.removeItem(RECENTS_KEY);
  };

  const load = async (reset) => {
    setLoading(true);
    try {
      const res = await fetchFor(tab, activeFilter, reset ? null : cursor, debouncedQ || undefined);
      const next = res?.items || [];
      setItems((prev) => (reset ? next : [...prev, ...next]));
      setCursor(res?.next_cursor || null);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Couldn't load discovery.");
      if (reset) setItems([]);
      setCursor(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  // Reset + fetch first page whenever the tab, (valid) filter, or search query changes.
  useEffect(() => {
    setItems([]); setCursor(null); setLoaded(false);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activeFilter, debouncedQ]);

  const switchTab = (t) => { setParams({ tab: t }); setFilter(FILTERS[t][0]); };

  const openItem = (it) => {
    if (q.trim()) pushRecent(q);
    if (tab === "bloqs") navigate(`/app/bloqs/${it.id}`);
    else if (tab === "channels") navigate(`/app/channels/${it.id}`);
    else navigate(`/u/${it.handle}`);
  };

  // Search is server-side (`q=` on /discover/*): results are the full catalog, filtered + paginated by the backend.
  const qActive = q.trim().length > 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col bg-bloq-bg" data-testid="discover-page">
      <div className="flex shrink-0 items-center gap-3 border-b border-white/8 bg-bloq-s1/60 px-5 py-3 backdrop-blur-xl">
        <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted hover:bg-white/8 hover:text-white md:hidden" data-testid="discover-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-bloq-mint/12 text-bloq-mint"><Compass className="h-5 w-5" /></div>
        <div>
          <div className="font-semibold">Discover</div>
          <div className="text-xs text-bloq-muted">Verified bloqs, channels & the most-trusted people on BLOQ</div>
        </div>
      </div>

      {/* Search */}
      <div className="shrink-0 px-4 pt-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-bloq-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") pushRecent(q); }}
            placeholder={`Search ${TABS.find((t) => t.key === tab)?.label.toLowerCase() || ""}…`}
            className="h-10 w-full rounded-full border border-white/12 bg-bloq-s1 pl-9 pr-9 text-sm text-white placeholder:text-bloq-muted focus:border-bloq-mint/50 focus:outline-none"
            data-testid="discover-search-input"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-bloq-muted hover:bg-white/10 hover:text-white"
              data-testid="discover-search-clear"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Recent searches — tap to re-run (shown when not actively searching) */}
      {!qActive && recents.length > 0 && (
        <div className="shrink-0 px-4 pt-2.5" data-testid="discover-recent-searches">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-muted">
              <Clock className="h-3 w-3" /> Recent
            </span>
            <button onClick={clearRecents} className="text-[11px] font-semibold text-bloq-muted transition-colors hover:text-white" data-testid="discover-recent-clear-all">
              Clear
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {recents.map((term, i) => (
              <span
                key={term}
                className="inline-flex items-center gap-1 rounded-full border border-white/12 bg-bloq-s1 py-1 pl-3 pr-1.5 text-xs text-white/85 transition-colors hover:border-bloq-mint/40"
                data-testid={`discover-recent-${i}`}
              >
                <button onClick={() => setQ(term)} className="max-w-[140px] truncate" data-testid={`discover-recent-chip-${i}`}>
                  {term}
                </button>
                <button
                  onClick={() => removeRecent(term)}
                  className="flex h-4 w-4 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-white/10 hover:text-white"
                  aria-label={`Remove ${term}`}
                  data-testid={`discover-recent-remove-${i}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex shrink-0 gap-1 border-b border-white/8 px-4 pt-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={cn("flex items-center gap-1.5 rounded-t-lg px-3 py-2 text-sm font-semibold transition-colors", tab === t.key ? "border-b-2 border-bloq-mint text-white" : "text-bloq-muted hover:text-white")}
            data-testid={`discover-tab-${t.key}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex shrink-0 flex-wrap gap-2 px-4 py-3">
        {FILTERS[tab].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn("rounded-full px-3 py-1.5 text-xs font-semibold transition-colors", activeFilter === f ? "bg-bloq-mint/15 text-bloq-mint" : "border border-white/12 text-bloq-muted hover:text-white")}
            data-testid={`discover-filter-${f}`}
          >
            {FILTER_LABEL[f]}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto px-4 pb-6">
        {loading && items.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-bloq-muted"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : loaded && !loading && items.length === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center text-center text-bloq-muted" data-testid="discover-empty">
            <Compass className="mb-2 h-8 w-8 opacity-50" />
            {qActive
              ? <>No {TABS.find((t) => t.key === tab)?.label.toLowerCase()} matching “{q.trim()}”.</>
              : <>Nothing here yet for “{FILTER_LABEL[activeFilter]}”.</>}
          </div>
        ) : (
          <>
            {items.map((it, idx) =>
              tab === "bloqs" ? <BloqCard key={it.id || idx} c={it} onOpen={() => openItem(it)} />
                : tab === "channels" ? <ChannelCard key={it.id || idx} c={it} onOpen={() => openItem(it)} />
                  : <PersonCard key={it.handle || it.id || idx} p={it} onOpen={() => openItem(it)} />,
            )}
            {cursor && (
              <button
                onClick={() => load(false)}
                disabled={loading}
                className="mt-2 w-full rounded-full border border-white/12 py-2.5 text-sm font-semibold text-bloq-mint transition-colors hover:bg-bloq-mint/10 disabled:opacity-50"
                data-testid="discover-load-more"
              >
                {loading ? "Loading…" : "Load more"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
